// src/pages/Competitions.js - competitions a leader can enter.
//
// Audit fixes: FL-21 (registration state per competition: open / opens on a date /
// closed, deadline countdown), FL-07 (retry), FL-35 (shared API client instead of raw
// axios), FL-38 (Czech dates "5. 3. 2027", "Grand Finále"), §5.10 (own entries count).
// A round stays listed until its day; after the registration deadline it says "Registrace
// uzavřena". Pre-registration rounds offer "Předregistrovat choreografii", nearly full ones
// show the last free places.
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Award, CalendarDays, ChevronRight, MapPin } from 'lucide-react';
import useFetch from '../hooks/useFetch';
import { choreographiesApi, competitionsApi } from '../services/api';
import { formatDateLong, countLabel, isPastCompetition, isRegistrationClosed, showInCompetitionLists } from '../utils/format';
import { EmptyState, PageHeader, Spinner } from '../components/ui';
import LoadError from '../components/leader/LoadError';
import {
  CapacityBadge,
  CompetitionTypeBadge,
  DeadlineText,
  RegistrationBadge,
  isFull,
} from '../components/leader/CompetitionBadges';

/** New entries are possible (not a Grand Finále, not over, registration not closed). */
const acceptsEntries = (competition) =>
  competition.type !== 'grand_finale' && !isPastCompetition(competition) && !isRegistrationClosed(competition);

function actionLabel(competition) {
  if (acceptsEntries(competition)) {
    if (competition.status === 'active' && !isFull(competition)) return 'Přihlásit choreografii';
    if (competition.status === 'upcoming') return 'Předregistrovat choreografii';
  }
  return 'Detail soutěže';
}

function CompetitionCard({ competition, entries }) {
  return (
    <Link
      to={`/competitions/${competition.id}`}
      className="flex h-full flex-col rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:p-5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <CompetitionTypeBadge type={competition.type} />
        <RegistrationBadge competition={competition} />
        <CapacityBadge competition={competition} />
      </div>
      <h2 className="mt-2 text-lg font-semibold text-gray-900">{competition.name}</h2>
      <div className="mt-2 space-y-1.5 text-sm text-gray-700">
        {competition.location && (
          <p className="flex items-center gap-1.5">
            <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
            {competition.location}
          </p>
        )}
        <p className="flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4 text-gray-500" aria-hidden="true" />
          {formatDateLong(competition.date)}
        </p>
        {!isRegistrationClosed(competition) && <DeadlineText date={competition.registrationEnd} />}
      </div>
      <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 pt-3 text-sm">
        <span className="text-gray-600">
          {entries > 0 ? `Vaše přihlášky: ${entries}` : 'Zatím bez vaší přihlášky'}
        </span>
        <span className="inline-flex items-center gap-1 font-medium text-indigo-700">
          {actionLabel(competition)}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

const Competitions = () => {
  const { data, error, isLoading, reload } = useFetch(async () => {
    const minePromise = choreographiesApi
      .getAll()
      .then((res) => res.data || [])
      .catch(() => []);
    const [competitionsRes, mine] = await Promise.all([competitionsApi.getAll(), minePromise]);
    return { competitions: competitionsRes.data || [], mine };
  }, 'all');

  const entriesByCompetition = useMemo(() => {
    const counts = {};
    (data?.mine || []).forEach((c) => {
      const key = c.competition?.id;
      if (key !== undefined) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [data]);

  if (isLoading && !data) return <Spinner label="Načítám soutěže…" />;
  if (error && !data) return <LoadError title="Soutěže se nepodařilo načíst" error={error} onRetry={reload} />;

  // after the registration deadline a round stays only for groups taking part in it
  const competitions = data.competitions.filter((c) => showInCompetitionLists(c, entriesByCompetition[c.id] || 0));
  const openCount = competitions.filter((c) => c.status === 'active' && !isRegistrationClosed(c)).length;

  return (
    <div>
      <PageHeader
        title="Soutěže"
        subtitle={
          competitions.length > 0
            ? `${countLabel(competitions.length, ['soutěž', 'soutěže', 'soutěží'])}, registrace otevřena: ${openCount}.`
            : undefined
        }
      />
      {competitions.length === 0 ? (
        <EmptyState icon={Award} title="Momentálně nejsou vypsané žádné soutěže">
          Nové soutěže zveřejníme na webu bestofthebest.cz.
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {competitions.map((competition) => (
            <CompetitionCard
              key={competition.id}
              competition={competition}
              entries={entriesByCompetition[competition.id] || 0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Competitions;
